import { useEffect, useState, useRef } from "react"

const CELL_SIZE = 100

export function GridOverlay() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ cols: 0, rows: 0 })

  useEffect(() => {
    function updateDimensions() {
      const wrapper = containerRef.current?.parentElement
      if (!wrapper) return
      const cols = Math.ceil(wrapper.scrollWidth / CELL_SIZE)
      const rows = Math.ceil(wrapper.scrollHeight / CELL_SIZE)
      setDimensions({ cols, rows })
    }

    updateDimensions()

    const wrapper = containerRef.current?.parentElement
    const observer = new ResizeObserver(updateDimensions)
    if (wrapper) {
      observer.observe(wrapper)
      // Also observe children changes that affect scrollHeight
      const mutationObserver = new MutationObserver(updateDimensions)
      mutationObserver.observe(wrapper, { childList: true, subtree: true })
      return () => {
        observer.disconnect()
        mutationObserver.disconnect()
      }
    }
    return () => observer.disconnect()
  }, [])

  const colLetters = (index: number): string => {
    let result = ""
    let n = index
    while (n >= 0) {
      result = String.fromCharCode(65 + (n % 26)) + result
      n = Math.floor(n / 26) - 1
    }
    return result
  }

  const totalWidth = dimensions.cols * CELL_SIZE
  const totalHeight = dimensions.rows * CELL_SIZE

  return (
    <div
      ref={containerRef}
      className="absolute top-0 left-0 z-[9999] pointer-events-none"
      style={{
        width: totalWidth,
        height: totalHeight,
      }}
    >
      {/* Vertical lines */}
      {Array.from({ length: dimensions.cols + 1 }, (_, i) => (
        <div
          key={`v-${i}`}
          className="absolute top-0 border-l border-blue-400/30"
          style={{ left: i * CELL_SIZE, height: totalHeight }}
        />
      ))}
      {/* Horizontal lines */}
      {Array.from({ length: dimensions.rows + 1 }, (_, i) => (
        <div
          key={`h-${i}`}
          className="absolute left-0 border-t border-blue-400/30"
          style={{ top: i * CELL_SIZE, width: totalWidth }}
        />
      ))}
      {/* Coordinate labels */}
      {Array.from({ length: dimensions.rows }, (_, row) =>
        Array.from({ length: dimensions.cols }, (_, col) => (
          <div
            key={`label-${row}-${col}`}
            className="absolute"
            style={{
              left: col * CELL_SIZE + 2,
              top: row * CELL_SIZE + 1,
            }}
          >
            <span className="text-[10px] font-mono leading-none bg-blue-500/80 text-white px-1 py-0.5 rounded-br">
              {colLetters(col)}{row + 1}
            </span>
          </div>
        ))
      )}
    </div>
  )
}
